import type { PartnerCockpitOfferRequest } from '@houzy/schemas/partner-cockpit/offer-requests';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useTrackEvent, type OfferRequestList } from '../../../lib/trackingEvents';
import { minutesSinceListed } from '../offerRequestTracking';
import { recordOfferRequestOpening } from './offerRequestsApi';

type Opening = { offerRequestLeadUuid: string; lead: PartnerCockpitOfferRequest; list: OfferRequestList };

// Recorded once per lead per visit: the effect re-runs on every render pass, and React's development
// double-mount would otherwise count the same opening twice. Waits for the lead itself, which the event describes.
export function useRecordOfferRequestOpening(
    offerRequestLeadUuid: string,
    lead: PartnerCockpitOfferRequest | undefined,
    list: OfferRequestList,
): void {
    const recordedUuid = useRef<string | null>(null);
    const track = useTrackEvent();
    // Tracked from the mutation rather than a `mutate` callback, which is dropped once the page remounts before
    // the response arrives (as React's development double-mount always does).
    const { mutate } = useMutation({
        mutationFn: (opening: Opening) => recordOfferRequestOpening(opening.offerRequestLeadUuid),
        onSuccess: ({ isFirstOpening }, opening) =>
            track('Offer Request Opened', {
                offerRequestId: opening.lead.referenceId,
                minutesSinceListed: minutesSinceListed(opening.lead.matchedAt),
                isFirstOpening,
                list: opening.list,
            }),
    });

    useEffect(() => {
        if (!lead || recordedUuid.current === offerRequestLeadUuid) {
            return;
        }
        recordedUuid.current = offerRequestLeadUuid;
        mutate({ offerRequestLeadUuid, lead, list });
    }, [lead, list, offerRequestLeadUuid, mutate]);
}
